/**
 * API 본문 `translations` / `*Translations` — 공식 5어(ko, en, ar, id, mn).
 * 엔터티(클래스명 등)는 UI i18n JSON( public/locales )과 **별도**; 입력은 폼/수기·동일 복제 expand.
 * UI 편집 locale은 `i18n.language`를 `resolveLocaleForEntityForm으로 쿼한 슬롯(별도 “입력 언어” 드롭다운 없음).
 * 저장은 슬롯 5개(ko·en·ar·id·mn) `translations`로 직렬화(비어 있으면 빈 문자열).
 */
import {
	API_TRANSLATION_LOCALE_CODES,
	type ApiTranslationLocaleCode,
} from '@/config/locales';
import type { ClassTranslationInput } from '@/types/api/class';
import type {
	LectureTranslationInput,
	ProductPriceTranslationInput,
	ProductTranslationInput,
} from '@/types/api/product';
import type { BlogPostTranslationInput } from '@/types/api/blog-post';
import type { ResourcePostTranslationInput } from '@/types/api/resource-post';
import type {
	LearningObjectiveTranslationInput,
	OldScenarioMetaTranslationInput,
	ScenarioTranslationInput,
} from '@/types/api/scenario';
import { normalizeLocaleTag } from '@/lib/i18nLocale';
import { translateEntityFields, type EntityTranslationFields, type EntityTranslationResult } from '@/lib/entityAutoTranslate';

type OfficialLocaleCode = ApiTranslationLocaleCode;

export const API_TRANSLATION_LANGUAGES: readonly OfficialLocaleCode[] = [...API_TRANSLATION_LOCALE_CODES];

function debugTranslationPayload(entity: string, rows: Array<{ language?: string } & object>): void {
	if (process.env.NODE_ENV !== 'development') return;
	const languages = rows.map((row) => String(row.language ?? ''));
	const fieldPresence = rows.map((row) => ({
		language: String(row.language ?? ''),
		fields: Object.fromEntries(
			Object.entries(row as Record<string, unknown>)
				.filter(([key]) => key !== 'language')
				.map(([key, value]) => [
					key,
					typeof value === 'string' ? value.trim().length > 0 : value != null,
				])
		),
	}));
	// #region agent log
	fetch('http://127.0.0.1:7671/ingest/86a97c59-29dc-4de8-9eda-f49b3d766117',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'804701'},body:JSON.stringify({sessionId:'804701',runId:'pre-check',hypothesisId:'H3',location:'src/lib/apiTranslations.ts:debugTranslationPayload',message:'translation payload built',data:{entity,count:rows.length,languages,expectedLanguages:[...API_TRANSLATION_LANGUAGES],hasExpectedLanguageOrder:languages.join(',')===API_TRANSLATION_LANGUAGES.join(','),fieldPresence},timestamp:Date.now()})}).catch(()=>{});
	// #endregion
}

export type TitleDescTriple = {
	[Lng in OfficialLocaleCode]: { title: string; description: string; offlineClassDescription?: string };
};

const emptyTitleDesc = (): { title: string; description: string; offlineClassDescription?: string } => ({ title: '', description: '', offlineClassDescription: '' });

export function createEmptyTitleDescTriple(): TitleDescTriple {
	return {
		ko: emptyTitleDesc(),
		en: emptyTitleDesc(),
		ar: emptyTitleDesc(),
		id: emptyTitleDesc(),
		mn: emptyTitleDesc(),
	};
}

/**
 * `resolveLocaleForEntityForm` 때문에 폼이 **한 열(현재 UI 언어)** 만 채울 때,
 * 나머지 슬롯이 빈 채로 API에 가면 `translations` 검증 실패·노출 누락이 난다.
 * 제목이 비어 있는 슬롯·설명이 비어 있는 슬롯에 **첫로 채워진 제목이 있는 locale**(`primary`)의 title·description을 복제한다(P1 동일 복제).
 */
export function coalesceEmptyTitleDescSlots(triple: TitleDescTriple): TitleDescTriple {
	const primary = API_TRANSLATION_LANGUAGES.find((c) => triple[c].title.trim());
	if (primary == null) {
		return { ...triple };
	}
	const pTitle = triple[primary].title;
	const pDesc = triple[primary].description;
	const pOfflineDesc = triple[primary].offlineClassDescription ?? '';
	
	const out = createEmptyTitleDescTriple();
	for (const c of API_TRANSLATION_LANGUAGES) {
		const t = triple[c].title;
		const d = triple[c].description;
		const o = triple[c].offlineClassDescription;
		out[c] = {
			title: t.trim() ? t : pTitle,
			description: d.trim() ? d : pDesc,
			offlineClassDescription: o?.trim() ? o : pOfflineDesc,
		};
	}
	return out;
}

function resolveSourceLocaleFromTriple<T extends Record<OfficialLocaleCode, Record<string, string>>>(
	triple: T,
	preferredLocale?: string,
	hasContent?: (row: T[OfficialLocaleCode]) => boolean
): OfficialLocaleCode {
	const pref = normalizeLocaleTag(preferredLocale || '');
	if (API_TRANSLATION_LANGUAGES.includes(pref as any) && (hasContent?.(triple[pref as OfficialLocaleCode]) ?? Object.values(triple[pref as OfficialLocaleCode]).some(Boolean))) {
		return pref as OfficialLocaleCode;
	}
	const found = API_TRANSLATION_LANGUAGES.find((code) => hasContent?.(triple[code]) ?? Object.values(triple[code]).some(Boolean));
	return found ?? 'en';
}

function normalizeComparableText(value: string | undefined): string {
	return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function shouldFillString(current: string | undefined, source: string, isSourceLocale = false): boolean {
	const sourceText = normalizeComparableText(source);
	if (!sourceText) return false;
	const currentText = normalizeComparableText(current);
	if (!currentText) return true;
	/**
	 * Some single-language forms intentionally start from `expand*ToTriple`,
	 * which clones the source text to ko/en/ar only to satisfy DTO shape.
	 * Treat those source-copy target slots as auto-fillable, but never change
	 * the actual source slot.
	 */
	return !isSourceLocale && currentText === sourceText;
}

async function translateEntityFieldsOrFallback<T extends EntityTranslationFields>(
	sourceLanguage: OfficialLocaleCode,
	fields: T
): Promise<EntityTranslationResult<T>> {
	try {
		return await translateEntityFields(sourceLanguage, fields);
	} catch (error) {
		if (process.env.NODE_ENV === 'development') {
			console.warn('[apiTranslations] auto translation failed; saving source text as fallback.', error);
		}
		return {
			ko: { ...fields },
			en: { ...fields },
			ar: { ...fields },
			id: { ...fields },
			mn: { ...fields },
		};
	}
}

export async function translateTitleDescTripleIfNeeded(
	triple: TitleDescTriple,
	sourceLocale?: string
): Promise<TitleDescTriple> {
	const source = resolveSourceLocaleFromTriple(
		triple,
		sourceLocale,
		(row) => Boolean(row.title.trim() || row.description.trim())
	);
	const sourceFields: Record<string, string> = {
		title: triple[source].title.trim(),
		description: triple[source].description.trim(),
	};
	
	if (triple[source].offlineClassDescription?.trim()) {
		sourceFields.offlineClassDescription = triple[source].offlineClassDescription.trim();
	}
	
	if (!sourceFields.title && !sourceFields.description) {
		return coalesceEmptyTitleDescSlots(triple);
	}
	
	const translated = await translateEntityFieldsOrFallback(source, sourceFields);
	
	const out: TitleDescTriple = {
		ko: { ...triple.ko },
		en: { ...triple.en },
		ar: { ...triple.ar },
		id: { ...triple.id },
		mn: { ...triple.mn },
	};
	
	for (const code of API_TRANSLATION_LANGUAGES) {
		if (shouldFillString(out[code].title, sourceFields.title, code === source)) {
			out[code].title = translated[code].title || sourceFields.title;
		}
		if (shouldFillString(out[code].description, sourceFields.description, code === source)) {
			out[code].description = translated[code].description || sourceFields.description;
		}
		if (sourceFields.offlineClassDescription) {
			const currentOffline = out[code].offlineClassDescription ?? '';
			if (shouldFillString(currentOffline, sourceFields.offlineClassDescription, code === source)) {
				out[code].offlineClassDescription = (translated[code] as any).offlineClassDescription || sourceFields.offlineClassDescription;
			}
		}
	}
	
	return coalesceEmptyTitleDescSlots(out);
}

function rowFor<T extends { language: string }>(
	list: T[] | undefined,
	code: string
): T | undefined {
	for (const row of list ?? []) {
		if (normalizeLocaleTag(row.language) === code) {
			return row;
		}
	}
	return undefined;
}

/**
 * API에서 내려온 `translations` + (선택) 단일 title·설명(레거시) → 3슬롯.
 * 단일 필드만 있으면 `ko`에 둔다(나머지 '').
 */
export function mergeTitleDescriptionTripleFromClassLike(
	legacyTitle: string | undefined,
	legacyDescription: string | undefined,
	translations: ClassTranslationInput[] | undefined
): TitleDescTriple {
	const base = createEmptyTitleDescTriple();
	for (const code of API_TRANSLATION_LANGUAGES) {
		const tr = rowFor(translations, code) as ClassTranslationInput | undefined;
		if (tr) {
			base[code] = {
				title: tr.title ?? '',
				description: (tr.description ?? '') || '',
				offlineClassDescription: (tr.offlineClassDescription ?? '') || '',
			};
		}
	}
	if (!translations?.length && (legacyTitle || legacyDescription)) {
		base.ko = { title: legacyTitle ?? '', description: legacyDescription ?? '', offlineClassDescription: '' };
	}
	return base;
}

/** `ProductTranslationInput[]` (구조가 Class와 동일) */
export function mergeTitleDescriptionTripleFromProductLike(
	legacyTitle: string | undefined,
	legacyDescription: string | undefined,
	translations: import('@/types/api/product').ProductTranslationInput[] | undefined
): TitleDescTriple {
	return mergeTitleDescriptionTripleFromClassLike(legacyTitle, legacyDescription, translations);
}

export function buildClassTranslationTriple(
	slots: TitleDescTriple
): ClassTranslationInput[] {
	const rows = API_TRANSLATION_LANGUAGES.map((code) => {
		const s = slots[code];
		const b: ClassTranslationInput = {
			language: code,
			title: s.title.trim(),
		};
		const d = s.description.trim();
		if (d) b.description = d;
		const ocd = s.offlineClassDescription?.trim();
		if (ocd) b.offlineClassDescription = ocd;
		return b;
	});
	debugTranslationPayload('class', rows);
	return rows;
}

export function buildProductTranslationTriple(
	by: TitleDescTriple
): ProductTranslationInput[] {
	const rows = API_TRANSLATION_LANGUAGES.map((code) => {
		const s = by[code];
		const p: ProductTranslationInput = { language: code, title: s.title.trim() };
		const d = s.description.trim();
		if (d) p.description = d;
		return p;
	});
	debugTranslationPayload('product', rows);
	return rows;
}

export function buildLectureTranslationTriple(
	byLang: { [K in OfficialLocaleCode]: { title: string; videoUrl: string; description: string } }
): LectureTranslationInput[] {
	const rows = API_TRANSLATION_LANGUAGES.map((code) => {
		const s = byLang[code];
		return {
			language: code,
			title: s.title.trim(),
			videoUrl: s.videoUrl.trim(),
			...(s.description.trim() ? { description: s.description.trim() } : {}),
		} satisfies LectureTranslationInput;
	});
	debugTranslationPayload('lecture', rows);
	return rows;
}

export function buildProductPriceTranslationTriple(
	by: TitleDescTriple
): ProductPriceTranslationInput[] {
	const rows = API_TRANSLATION_LANGUAGES.map((code) => {
		const s = by[code];
		return { language: code, title: s.title.trim() };
	});
	debugTranslationPayload('productPrice', rows);
	return rows;
}

export type ResourceTriple = {
	[K in OfficialLocaleCode]: { title: string; description: string; content: string };
};

export function createEmptyResourceTriple(): ResourceTriple {
	const z = { title: '', description: '', content: '' };
	return { ko: { ...z }, en: { ...z }, ar: { ...z }, id: { ...z }, mn: { ...z } };
}

export function mergeResourceTriple(
	translations: ResourcePostTranslationInput[] | undefined
): ResourceTriple {
	const t = createEmptyResourceTriple();
	for (const code of API_TRANSLATION_LANGUAGES) {
		const tr = rowFor(translations, code) as ResourcePostTranslationInput | undefined;
		if (tr) {
			t[code] = {
				title: tr.title ?? '',
				description: tr.description ?? '',
				content: tr.content ?? '',
			};
		}
	}
	return t;
}

export function buildResourcePostTranslationTriple(s: ResourceTriple): ResourcePostTranslationInput[] {
	const rows = API_TRANSLATION_LANGUAGES.map((code) => {
		const x = s[code];
		return {
			language: code,
			title: x.title.trim(),
			description: x.description.trim(),
			...(x.content.trim() ? { content: x.content.trim() } : {}),
		};
	});
	debugTranslationPayload('resourcePost', rows);
	return rows;
}

export async function translateResourceTripleIfNeeded(
	triple: ResourceTriple,
	sourceLocale?: string
): Promise<ResourceTriple> {
	const source = resolveSourceLocaleFromTriple(
		triple,
		sourceLocale,
		(row) => Boolean(row.title.trim() || row.description.trim() || row.content.trim())
	);
	const sourceFields = {
		title: triple[source].title.trim(),
		description: triple[source].description.trim(),
		content: triple[source].content.trim(),
	};
	if (!sourceFields.title && !sourceFields.description && !sourceFields.content) return triple;
	const translated = await translateEntityFieldsOrFallback(source, sourceFields);
	const out: ResourceTriple = {
		ko: { ...triple.ko },
		en: { ...triple.en },
		ar: { ...triple.ar },
		id: { ...triple.id },
		mn: { ...triple.mn },
	};
	for (const code of API_TRANSLATION_LANGUAGES) {
		if (shouldFillString(out[code].title, sourceFields.title, code === source)) out[code].title = translated[code].title || sourceFields.title;
		if (shouldFillString(out[code].description, sourceFields.description, code === source)) {
			out[code].description = translated[code].description || sourceFields.description;
		}
		if (shouldFillString(out[code].content, sourceFields.content, code === source)) out[code].content = translated[code].content || sourceFields.content;
	}
	return out;
}

export type BlogTriple = { [K in OfficialLocaleCode]: { title: string; content: string } };

export function createEmptyBlogTriple(): BlogTriple {
	return { 
		ko: { title: '', content: '' }, 
		en: { title: '', content: '' }, 
		ar: { title: '', content: '' },
		id: { title: '', content: '' },
		mn: { title: '', content: '' }
	};
}


export function mergeBlogTriple(
	legacyTitle: string | undefined,
	legacyContent: string | undefined,
	translations: BlogPostTranslationInput[] | undefined
): BlogTriple {
	const t = createEmptyBlogTriple();
	for (const code of API_TRANSLATION_LANGUAGES) {
		const tr = rowFor(translations, code) as BlogPostTranslationInput | undefined;
		if (tr) {
			t[code] = { title: tr.title ?? '', content: tr.content ?? '' };
		}
	}
	if (!translations?.length && (legacyTitle || legacyContent)) {
		t.ko = { title: legacyTitle ?? '', content: legacyContent ?? '' };
	}
	return t;
}

export function buildBlogPostTranslationTriple(s: BlogTriple): BlogPostTranslationInput[] {
	const rows = API_TRANSLATION_LANGUAGES.map((code) => {
		const x = s[code];
		return { language: code, title: x.title.trim(), content: x.content.trim() };
	});
	debugTranslationPayload('blogPost', rows);
	return rows;
}

export async function translateBlogTripleIfNeeded(
	triple: BlogTriple,
	sourceLocale?: string
): Promise<BlogTriple> {
	const source = resolveSourceLocaleFromTriple(
		triple,
		sourceLocale,
		(row) => Boolean(row.title.trim() || row.content.trim())
	);
	const sourceFields = {
		title: triple[source].title.trim(),
		content: triple[source].content.trim(),
	};
	if (!sourceFields.title && !sourceFields.content) return triple;
	const translated = await translateEntityFieldsOrFallback(source, sourceFields);
	const out: BlogTriple = {
		ko: { ...triple.ko },
		en: { ...triple.en },
		ar: { ...triple.ar },
		id: { ...triple.id },
		mn: { ...triple.mn },
	};
	for (const code of API_TRANSLATION_LANGUAGES) {
		if (shouldFillString(out[code].title, sourceFields.title, code === source)) out[code].title = translated[code].title || sourceFields.title;
		if (shouldFillString(out[code].content, sourceFields.content, code === source)) out[code].content = translated[code].content || sourceFields.content;
	}
	return out;
}

/** 시나리오(구) 폼이 한 벌로만 있을 때 모든 언어에 동일 복제 */
export function expandScenarioI18nToOfficialTriple(v: {
	title: string;
	handoverNote: string;
	modeValueTitle: string;
}): { [K in OfficialLocaleCode]: { title: string; handoverNote: string; modeValueTitle: string } } {
	return { ko: { ...v }, en: { ...v }, ar: { ...v }, id: { ...v }, mn: { ...v } };
}

export function expandLearningObjectiveTitleToTriple(title: string): { [K in OfficialLocaleCode]: { title: string } } {
	const t = (title ?? '').trim();
	return { ko: { title: t }, en: { title: t }, ar: { title: t }, id: { title: t }, mn: { title: t } };
}

export function expandOldMetaTitleToTriple(title: string): { [K in OfficialLocaleCode]: { title: string } } {
	const t = (title ?? '').trim() || '-';
	return { ko: { title: t }, en: { title: t }, ar: { title: t }, id: { title: t }, mn: { title: t } };
}

export function buildScenarioTranslationTriple(
	by: {
		[K in OfficialLocaleCode]: { title: string; handoverNote: string; modeValueTitle: string };
	}
): ScenarioTranslationInput[] {
	const rows = API_TRANSLATION_LANGUAGES.map((code) => {
		const s = by[code];
		return {
			language: code,
			title: s.title.trim(),
			...(s.handoverNote.trim() ? { handoverNote: s.handoverNote.trim() } : {}),
			...(s.modeValueTitle.trim() ? { modeValueTitle: s.modeValueTitle.trim() } : {}),
		};
	});
	debugTranslationPayload('scenario', rows);
	return rows;
}

export async function translateScenarioTripleIfNeeded(
	by: {
		[K in OfficialLocaleCode]: { title: string; handoverNote: string; modeValueTitle: string };
	},
	sourceLocale?: string
): Promise<{ [K in OfficialLocaleCode]: { title: string; handoverNote: string; modeValueTitle: string } }> {
	const source = resolveSourceLocaleFromTriple(
		by,
		sourceLocale,
		(row) => Boolean(row.title.trim() || row.handoverNote.trim() || row.modeValueTitle.trim())
	);
	const sourceFields = {
		title: by[source].title.trim(),
		handoverNote: by[source].handoverNote.trim(),
		modeValueTitle: by[source].modeValueTitle.trim(),
	};
	if (!sourceFields.title && !sourceFields.handoverNote && !sourceFields.modeValueTitle) return by;
	const translated = await translateEntityFieldsOrFallback(source, sourceFields);
	const out = {
		ko: { ...by.ko },
		en: { ...by.en },
		ar: { ...by.ar },
		id: { ...by.id },
		mn: { ...by.mn },
	};
	for (const code of API_TRANSLATION_LANGUAGES) {
		if (shouldFillString(out[code].title, sourceFields.title, code === source)) out[code].title = translated[code].title || sourceFields.title;
		if (shouldFillString(out[code].handoverNote, sourceFields.handoverNote, code === source)) {
			out[code].handoverNote = translated[code].handoverNote || sourceFields.handoverNote;
		}
		if (shouldFillString(out[code].modeValueTitle, sourceFields.modeValueTitle, code === source)) {
			out[code].modeValueTitle = translated[code].modeValueTitle || sourceFields.modeValueTitle;
		}
	}
	return out;
}

export function buildLearningObjectiveTranslationTriple(
	by: { [K in OfficialLocaleCode]: { title: string } }
): LearningObjectiveTranslationInput[] {
	const rows = API_TRANSLATION_LANGUAGES.map((code) => ({ language: code, title: by[code].title.trim() }));
	debugTranslationPayload('learningObjective', rows);
	return rows;
}

export async function translateTitleOnlyTripleIfNeeded(
	by: { [K in OfficialLocaleCode]: { title: string } },
	sourceLocale?: string
): Promise<{ [K in OfficialLocaleCode]: { title: string } }> {
	const source = resolveSourceLocaleFromTriple(by, sourceLocale, (row) => Boolean(row.title.trim()));
	const sourceFields = { title: by[source].title.trim() };
	if (!sourceFields.title) return by;
	const translated = await translateEntityFieldsOrFallback(source, sourceFields);
	const out = { ko: { ...by.ko }, en: { ...by.en }, ar: { ...by.ar }, id: { ...by.id }, mn: { ...by.mn } };
	for (const code of API_TRANSLATION_LANGUAGES) {
		if (shouldFillString(out[code].title, sourceFields.title, code === source)) out[code].title = translated[code].title || sourceFields.title;
	}
	return out;
}

export function buildOldScenarioMetaTranslationTriple(
	by: { [K in OfficialLocaleCode]: { title: string } }
): OldScenarioMetaTranslationInput[] {
	const rows = API_TRANSLATION_LANGUAGES.map((code) => ({ language: code, title: by[code].title.trim() || '-' }));
	debugTranslationPayload('oldScenarioMeta', rows);
	return rows;
}

/** UI가 단일 문구만 있을 때 API 요구를 맞추기 위한 임시 동일 복제(제목·가격명 등) */
export function expandTitleToTripleField(title: string, description: string = ''): TitleDescTriple {
	const t = title;
	const d = description;
	return { 
		ko: { title: t, description: d }, 
		en: { title: t, description: d }, 
		ar: { title: t, description: d },
		id: { title: t, description: d },
		mn: { title: t, description: d }
	};
}

export function expandLectureToOfficialTriple(v: { title: string; videoUrl: string; description?: string }): {
	[K in OfficialLocaleCode]: { title: string; videoUrl: string; description: string };
} {
	const t = (v.title ?? '').trim();
	const u = (v.videoUrl ?? '').trim();
	const d = (v.description ?? '').trim();
	return {
		ko: { title: t, videoUrl: u, description: d },
		en: { title: t, videoUrl: u, description: d },
		ar: { title: t, videoUrl: u, description: d },
		id: { title: t, videoUrl: u, description: d },
		mn: { title: t, videoUrl: u, description: d },
	};
}

export type LectureFieldsTriple = {
	[K in OfficialLocaleCode]: { title: string; videoUrl: string; description: string };
};

export async function translateLectureFieldsTripleIfNeeded(
	triple: LectureFieldsTriple,
	sourceLocale?: string
): Promise<LectureFieldsTriple> {
	const source = resolveSourceLocaleFromTriple(
		triple,
		sourceLocale,
		(row) => Boolean(row.title.trim() || row.description.trim())
	);
	const sourceFields = {
		title: triple[source].title.trim(),
		description: triple[source].description.trim(),
	};
	if (!sourceFields.title && !sourceFields.description) return triple;
	const translated = await translateEntityFieldsOrFallback(source, sourceFields);
	const out: LectureFieldsTriple = {
		ko: { ...triple.ko },
		en: { ...triple.en },
		ar: { ...triple.ar },
		id: { ...triple.id },
		mn: { ...triple.mn },
	};
	for (const code of API_TRANSLATION_LANGUAGES) {
		if (shouldFillString(out[code].title, sourceFields.title, code === source)) out[code].title = translated[code].title || sourceFields.title;
		if (shouldFillString(out[code].description, sourceFields.description, code === source)) {
			out[code].description = translated[code].description || sourceFields.description;
		}
		if (!out[code].videoUrl.trim()) out[code].videoUrl = triple[source].videoUrl;
	}
	return out;
}

export function mergeLectureFieldsTriple(
	legacy: { title: string; videoUrl: string; description: string },
	translations: LectureTranslationInput[] | undefined
): LectureFieldsTriple {
	const base: LectureFieldsTriple = {
		ko: { title: '', videoUrl: '', description: '' },
		en: { title: '', videoUrl: '', description: '' },
		ar: { title: '', videoUrl: '', description: '' },
		id: { title: '', videoUrl: '', description: '' },
		mn: { title: '', videoUrl: '', description: '' },
	};
	for (const code of API_TRANSLATION_LANGUAGES) {
		const tr = rowFor(translations, code) as LectureTranslationInput | undefined;
		if (tr) {
			const desc = (tr.description ?? '') as string;
			base[code] = {
				title: (tr.title ?? '') as string,
				videoUrl: (tr.videoUrl ?? '') as string,
				description: typeof desc === 'string' ? desc : '',
			};
		}
	}
	const hasAny = API_TRANSLATION_LANGUAGES.some(
		(c) => base[c].title || base[c].videoUrl || base[c].description
	);
	if (!hasAny && (legacy.title || legacy.videoUrl || legacy.description)) {
		return expandLectureToOfficialTriple(legacy);
	}
	if (!hasAny) {
		return expandLectureToOfficialTriple({ title: '', videoUrl: '', description: '' });
	}
	return base;
}

export function expandPriceNameToTitleTriple(name: string): TitleDescTriple {
	return expandTitleToTripleField((name ?? '').trim(), '');
}

/**
 * GET 상세가 단일 locale 만 줄 때 — 해당 lang 슬롯에만 채우고 나머지 ''.
 */
export function mergeResourceTripleFromSingleDetail(
	lang: string,
	title: string,
	description: string,
	content: string | null | undefined
): ResourceTriple {
	const t = createEmptyResourceTriple();
	const code = normalizeLocaleTag(lang);
	const slot: OfficialLocaleCode = API_TRANSLATION_LANGUAGES.includes(code as any) ? (code as OfficialLocaleCode) : 'ko';
	t[slot] = { title, description, content: content ?? '' };
	return t;
}
